import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type JSX,
  type RefObject,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FolderOpen, ImagePlus, Loader2, Upload } from "lucide-react";
import type { MediaFolder } from "@ckgo2/shared/types";
import DestinationPicker from "@/components/common/DestinationPicker";
import { MEDIA_SOURCES_KEY, useMediaSources } from "@/hooks/useMediaSources";
import BatchFileRow from "./BatchFileRow";
import BatchSummaryBar from "./BatchSummaryBar";
import {
  batchPanelState,
  batchStartOptions,
  canRetryAll,
  shouldRefreshSources,
  type BatchPanelMode,
  type BatchPanelState,
} from "./BatchUploadPanel.model";
import { filesFromDataTransfer } from "./batchFiles";
import type { BatchUpload } from "./batchUpload.types";

export interface BatchUploadPanelProps {
  batch: BatchUpload;
  mode: BatchPanelMode;
  folder: MediaFolder;
  initialCountryIds: string[];
  selectedIds?: readonly string[];
  /** Klik na `[data-batch-done]` ve stavu `finished`; bez něj panel zavolá `batch.reset()`. */
  onDone?: () => void;
}

/**
 * `webkitdirectory` (výběr celé složky) `@types/react` nezná; rozbalený do props ho React
 * propustí do DOM i ve statickém renderu (ref callback by ho v něm nevykreslil).
 */
const FOLDER_INPUT_ATTRS = { webkitdirectory: "" } as Record<string, string>;

/**
 * Chrome na macOS u samotného `image/*` HEIC v dialogu nenabízí, proto přípony. Co z výběru
 * do dávky projde, rozhoduje model (`acceptBatchFiles`), ne tenhle filtr dialogu.
 */
const FILE_INPUT_ACCEPT = "image/*,.heic,.heif,.tif,.tiff";

/**
 * Panel dávky nahrávání (stránka Média i záložka Nahrát ve výběru fotek). Stav dávky, kontrolu
 * duplicit, zmenšení a nahrávání drží model (`useBatchUpload`); panel ho ukazuje a volá jeho
 * akce. Je to `<section>`, ne `<form>`: ve výběru fotek leží uvnitř formuláře editoru, proto
 * má každé tlačítko `type="button"`.
 */
export default function BatchUploadPanel({
  batch,
  mode,
  folder,
  initialCountryIds,
  selectedIds,
  onDone,
}: BatchUploadPanelProps): JSX.Element {
  const { t } = useTranslation();
  const state = batchPanelState(batch);
  const acceptsFiles = state === "idle" || state === "ready";
  // Čtení přetažené složky je asynchronní: o přijetí rozhoduje stav po dočtení, ne při puštění
  // (dávka mezitím mohla začít nebo doběhnout; `add()` po konci by ji bez „Hotovo“ smazal).
  const acceptsFilesRef = useRef(acceptsFiles);
  useLayoutEffect(() => {
    acceptsFilesRef.current = acceptsFiles;
  });
  const [dragging, setDragging] = useState(false);
  // Počet přetažení, jejichž čtení ještě běží. Start dávky do dočtení čeká: jinak by se
  // dočtené soubory zahodily (dávka už běží) a uživatel by o nich nevěděl.
  const [readingDrops, setReadingDrops] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // FileList je živý: reset hodnoty ho vyprázdní, proto nejdřív kopie. Bez resetu by opětovný
  // výběr týchž souborů (nová dávka) `change` nevyvolal.
  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.currentTarget.files ?? []);
    e.currentTarget.value = "";
    if (files.length > 0) batch.add(files);
  };

  // Celý kořen ruší výchozí chování přetažení i ve stavech, kdy soubory nebere: soubor puštěný
  // mimo zónu by prohlížeč otevřel v kartě a admin opustil.
  const onDragOver = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (!acceptsFiles) e.dataTransfer.dropEffect = "none";
    else if (!dragging) setDragging(true);
  };
  const onDragLeave = (e: DragEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
  };
  // DataTransfer žije jen během události: položky se předají synchronně, bez await před tím.
  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragging(false);
    if (!acceptsFiles) return;
    setReadingDrops((n) => n + 1);
    void filesFromDataTransfer(e.dataTransfer)
      .then((files) => {
        if (files.length > 0 && acceptsFilesRef.current) batch.add(files);
      })
      .finally(() => setReadingDrops((n) => n - 1));
  };

  return (
    <section
      data-batch-upload=""
      data-state={state}
      data-started-ms={batch.startedAt ?? undefined}
      data-finished-ms={!batch.running && batch.finishedAt !== null ? batch.finishedAt : undefined}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className="space-y-3"
    >
      {acceptsFiles && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={FILE_INPUT_ACCEPT}
            data-batch-file-input=""
            className="hidden"
            tabIndex={-1}
            onChange={onInputChange}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            {...FOLDER_INPUT_ATTRS}
            data-batch-folder-input=""
            className="hidden"
            tabIndex={-1}
            onChange={onInputChange}
          />
        </>
      )}

      {state === "idle" && (
        <div
          className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-gray-500 transition-colors ${
            dragging
              ? "border-primary-500 bg-primary-50 dark:bg-primary-900/10"
              : "border-gray-300 dark:border-gray-600"
          }`}
        >
          <Upload className="h-8 w-8" aria-hidden="true" />
          <p className="text-sm">{t("mediaUpload.dropHint")}</p>
          <PickButtons fileInputRef={fileInputRef} folderInputRef={folderInputRef} />
        </div>
      )}

      {state === "ready" && (
        <div
          className={`flex flex-wrap items-center gap-3 rounded-lg border border-dashed px-3 py-2 text-sm text-gray-500 ${
            dragging
              ? "border-primary-500 bg-primary-50 dark:bg-primary-900/10"
              : "border-gray-300 dark:border-gray-600"
          }`}
        >
          <span className="flex-1">{t("mediaUpload.addMore")}</span>
          <PickButtons fileInputRef={fileInputRef} folderInputRef={folderInputRef} />
        </div>
      )}

      <p
        data-batch-rejected=""
        data-count={batch.rejected}
        hidden={batch.rejected === 0}
        className="text-sm text-amber-700 dark:text-amber-400"
      >
        {batch.rejected > 0 && t("mediaUpload.rejected", { count: batch.rejected })}
      </p>

      {batch.files.length > 0 && (
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {batch.files.map((f) => (
            <BatchFileRow key={f.key} file={f} mode={mode} state={state} batch={batch} />
          ))}
        </ul>
      )}

      {state !== "idle" && (
        <BatchOptionsBar
          batch={batch}
          state={state}
          mode={mode}
          folder={folder}
          initialCountryIds={initialCountryIds}
          selectedIds={selectedIds}
          readingDrop={readingDrops > 0}
        />
      )}

      {state === "finished" && (
        <BatchSummaryBar
          summary={batch.summary}
          canRetryAll={canRetryAll(batch.files)}
          mode={mode}
          onRetryAll={() => batch.retryAllFailed()}
          onDone={onDone ?? (() => batch.reset())}
        />
      )}
    </section>
  );
}

function PickButtons({
  fileInputRef,
  folderInputRef,
}: {
  fileInputRef: RefObject<HTMLInputElement | null>;
  folderInputRef: RefObject<HTMLInputElement | null>;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="btn-secondary btn-sm"
      >
        <ImagePlus className="h-4 w-4" aria-hidden="true" />
        {t("mediaUpload.chooseFiles")}
      </button>
      <button
        type="button"
        onClick={() => folderInputRef.current?.click()}
        className="btn-secondary btn-sm"
      >
        <FolderOpen className="h-4 w-4" aria-hidden="true" />
        {t("mediaUpload.chooseFolder")}
      </button>
    </div>
  );
}

/**
 * Volby dávky (země, zdroj) a start. Připojí se při přechodu z prázdného panelu a odpojí se
 * po `reset()`, takže každá dávka se předvyplní z aktuálních `initialCountryIds` bez efektu,
 * který by props synchronizoval do stavu. Drží zdroj, proto po každém doběhnutí dávky
 * (`finishedAt`, i po opakování) se zdrojem zneplatní nabídku zdrojů (`shouldRefreshSources`).
 * Během čtení přetažené složky (`readingDrop`) start čeká, aby se dočtené soubory nezahodily.
 */
function BatchOptionsBar({
  batch,
  state,
  mode,
  folder,
  initialCountryIds,
  selectedIds,
  readingDrop,
}: {
  batch: BatchUpload;
  state: Exclude<BatchPanelState, "idle">;
  mode: BatchPanelMode;
  folder: MediaFolder;
  initialCountryIds: readonly string[];
  selectedIds?: readonly string[];
  readingDrop: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const uid = useId();
  const queryClient = useQueryClient();
  const { data: sources = [] } = useMediaSources();
  const [countryIds, setCountryIds] = useState<string[]>(() => [...initialCountryIds]);
  const [source, setSource] = useState("");
  const editable = state === "ready";

  // Doběhnutí dávky ohlásí model novým `finishedAt` (Kontrakt sémantika 2); kdy zneplatnit,
  // rozhoduje `shouldRefreshSources` nad snímkem. Zdroj se po startu nemění; souhrn se po konci
  // změní jen obnovou z koše nebo odebráním, zneplatnění navíc neškodí.
  const { finishedAt, running, summary } = batch;
  useEffect(() => {
    if (shouldRefreshSources({ finishedAt, running, summary }, source)) {
      void queryClient.invalidateQueries({ queryKey: MEDIA_SOURCES_KEY });
    }
  }, [finishedAt, running, source, summary, queryClient]);

  const start = () =>
    batch.start(batchStartOptions({ mode, folder, countryIds, source, selectedIds }));

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div data-batch-country="">
          <label htmlFor={`${uid}-countries`} className="label">
            {t("mediaUpload.countries")}
          </label>
          <DestinationPicker
            id={`${uid}-countries`}
            mode="multiple"
            level="country"
            value={countryIds}
            onChange={setCountryIds}
            disabled={!editable}
          />
        </div>
        <div>
          <label htmlFor={`${uid}-source`} className="label">
            {t("mediaUpload.source")}
          </label>
          <input
            id={`${uid}-source`}
            type="text"
            data-batch-source=""
            maxLength={120}
            list={`${uid}-sources`}
            value={source}
            disabled={!editable}
            onChange={(e) => setSource(e.target.value)}
            // Enter by odeslal formulář editoru, v němž panel ve výběru fotek leží.
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            aria-describedby={`${uid}-source-hint`}
            className="input w-full"
          />
          <datalist id={`${uid}-sources`}>
            {sources.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <p id={`${uid}-source-hint`} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {mode === "page" ? t("mediaUpload.sourceHint") : t("mediaUpload.sourceHintPicker")}
          </p>
        </div>
      </div>
      {state === "ready" && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={() => batch.reset()} className="btn-secondary">
            {t("mediaUpload.cancel")}
          </button>
          <button
            type="button"
            data-batch-start=""
            disabled={batch.files.length === 0 || readingDrop}
            aria-busy={readingDrop || undefined}
            onClick={start}
            className="btn-primary"
          >
            {readingDrop && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t("mediaUpload.start", { count: batch.files.length })}
          </button>
        </div>
      )}
    </div>
  );
}
